import { Component, inject, OnInit, signal, computed, HostListener, effect } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../core/auth/auth.service';
import { NotificationService } from '../core/notifications/notification.service';
import { VersionCheckService } from '../core/services/version-check.service';
import { Role, ROLE_LABELS } from '../core/models/role.enum';
import { APP_VERSION } from '../core/version';
import { NotificationBellComponent } from '../shared/components/notification-bell/notification-bell';
import { ToastContainerComponent } from '../shared/components/toast-container/toast-container';
import { BreadcrumbComponent } from '../shared/components/breadcrumb/breadcrumb';
import { environment } from '../../environments/environment';

const LG_BREAKPOINT = 1024;

@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NotificationBellComponent, ToastContainerComponent, BreadcrumbComponent],
  templateUrl: './shell.html',
  styleUrl: './shell.css',
})
export class ShellComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  private readonly notifications = inject(NotificationService);
  private readonly versionCheck = inject(VersionCheckService);
  protected readonly sidebarOpen = signal(window.innerWidth >= LG_BREAKPOINT);
  protected readonly isDesktop = signal(window.innerWidth >= LG_BREAKPOINT);

  constructor() {
    effect(() => {
      if (this.sidebarOpen() && !this.isDesktop()) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    });
  }

  protected readonly isMock = environment.authMock;

  protected readonly allRoles = Object.values(Role).map((value) => ({
    value,
    label: ROLE_LABELS[value],
  }));

  protected readonly mockRoleSwitcherOpen = signal(false);

  protected readonly activeRoles = computed(() => this.auth.user()?.roles ?? []);

  protected toggleMockRole(role: string): void {
    const current = [...(this.auth.user()?.roles ?? [])];
    const idx = current.indexOf(role);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(role);
    }
    if (current.length === 0) current.push('admin');
    this.auth.switchMockRole(current);
  }

  protected readonly version = APP_VERSION;

  protected readonly navItems = [
    { path: '/dashboard', label: 'Áttekintés', roles: [] as Role[] },
    { path: '/assets', label: 'Eszközök', roles: [Role.ADMIN, Role.FLEET_MANAGER, Role.FLEET_VIEWER, Role.EQUIPMENT_MANAGER, Role.EQUIPMENT_VIEWER] },
    { path: '/hr', label: 'HR', roles: [Role.ADMIN, Role.HR_MANAGER, Role.HR_VIEWER] },
    { path: '/documents', label: 'Dokumentumtár', roles: [Role.ADMIN, Role.DOCUMENT_MANAGER, Role.DOCUMENT_VIEWER] },
    { path: '/settings', label: 'Értesítési beállítások', roles: [Role.ADMIN, Role.NOTIFICATION_MANAGER, Role.NOTIFICATION_VIEWER] },
  ];

  protected readonly visibleNavItems = computed(() =>
    this.navItems.filter((item) =>
      item.roles.length === 0 || this.auth.hasAnyRole(...item.roles)
    )
  );

  @HostListener('window:resize')
  protected handleResize(): void {
    const desktop = window.innerWidth >= LG_BREAKPOINT;
    const wasDesktop = this.isDesktop();
    this.isDesktop.set(desktop);
    if (desktop && !wasDesktop) {
      this.sidebarOpen.set(true);
    }
    if (!desktop && wasDesktop) {
      this.sidebarOpen.set(false);
    }
  }

  ngOnInit(): void {
    this.notifications.loadUpcoming().subscribe();
    this.versionCheck.startPolling();
  }

  protected toggleSidebar(): void {
    this.sidebarOpen.update((v) => !v);
  }

  protected closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  protected closeSidebarOnMobile(): void {
    if (!this.isDesktop()) {
      this.sidebarOpen.set(false);
    }
  }

  protected logout(): void {
    this.auth.logout().subscribe();
  }
}
